/**
 * @file EditEmployeeDialog.tsx
 * @brief Sheet form to edit employee profile, status, password, and card.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 * @updated 01-10-2026 — form standard: palette frame, Profile / Access /
 *   Security tabs as Edit user, status as the header pill
 */

import { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { TitlePicker } from "@/components/ui/TitlePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useAuth } from "@/shared/auth/auth-context";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import {
  mapPaymentMethods,
  useCompanyPaymentMethods,
} from "@/features/company-billing/api";
import { CardBrandIcon } from "@/features/company-billing/components/CardBrandIcon";
import { formatCardBrand } from "@/features/company-billing/components/cardBrand";
import {
  useAssignEmployeeCard,
  useResetEmployeePassword,
  useUpdateEmployee,
} from "../api";
import type { CompanyEmployee } from "../schemas";

/** Sentinel Select value meaning "no assignment → use company default". */
const DEFAULT_CARD_VALUE = "__default__";

interface EditEmployeeDialogProps {
  employee: CompanyEmployee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditEmployeeDialog({
  employee,
  open,
  onOpenChange,
}: EditEmployeeDialogProps) {
  const update = useUpdateEmployee();
  const resetPw = useResetEmployeePassword();
  const assignCard = useAssignEmployeeCard();

  const { user } = useAuth();
  const companyId = user?.company_id ?? user?.companyId ?? null;
  const cardsQuery = useCompanyPaymentMethods(companyId);
  const cards = mapPaymentMethods(cardsQuery.data);
  const canAssign = cards.length >= 2;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<"user" | "admin">("user");
  const [status, setStatus] = useState<"active" | "suspended">("active");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [assignedCard, setAssignedCard] = useState<string>(DEFAULT_CARD_VALUE);
  // Tabs, as Edit user (DES 01-10-2026: form standard).
  const [tab, setTab] = useState<EditEmployeeTab>("profile");

  useEffect(() => {
    if (!employee) return;
    setFirstName(employee.first_name);
    setLastName(employee.last_name);
    setTitle(employee.title ?? "");
    setRole(employee.role === "admin" ? "admin" : "user");
    setStatus(employee.status === "suspended" ? "suspended" : "active");
    setNewPassword("");
    setShowPassword(false);
    setAssignedCard(employee.assigned_payment_method_id ?? DEFAULT_CARD_VALUE);
    if (open) setTab("profile");
  }, [employee, open]);

  const saving =
    update.isPending || resetPw.isPending || assignCard.isPending;

  const displayName = employee
    ? formatFullName(employee.first_name, employee.last_name)
    : "";
  const headerName =
    displayName !== "—" && displayName
      ? displayName
      : (employee?.email ?? "Employee");

  const selectedCard =
    assignedCard === DEFAULT_CARD_VALUE
      ? null
      : (cards.find((c) => c.id === assignedCard) ?? null);

  const handleSave = async () => {
    if (!employee) return;
    try {
      await update.mutateAsync({
        id: employee.id,
        input: {
          first_name: firstName,
          last_name: lastName,
          title: title || undefined,
          role,
          status,
        },
      });
      if (newPassword.trim().length >= 8) {
        await resetPw.mutateAsync({
          id: employee.id,
          newPassword: newPassword.trim(),
        });
      }
      if (canAssign) {
        const current =
          employee.assigned_payment_method_id ?? DEFAULT_CARD_VALUE;
        if (assignedCard !== current) {
          await assignCard.mutateAsync({
            id: employee.id,
            paymentMethodId:
              assignedCard === DEFAULT_CARD_VALUE ? null : assignedCard,
          });
        }
      }
      toast({ variant: "success", title: "Employee updated" });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Opens like Cmd+K, as Edit user (DES 01-10-2026: form standard). */}
      <FormSheetContent side="palette">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <FormSheetFrame>
            <FormSheetHeader>
              {/* Status sits beside the title as a pill, as Edit company
                  (DES 01-10-2026: form standard). */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {employee ? (
                    <Avatar
                      src={employee.profile_image_url}
                      initials={getInitials(
                        employee.first_name,
                        employee.last_name,
                        employee.email,
                      )}
                      gender={employee.gender}
                      alt={headerName}
                      size="md"
                      className="shrink-0"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <FormSheetTitle className="truncate">
                      {employee ? headerName : "Edit employee"}
                    </FormSheetTitle>
                    <FormSheetDescription className="truncate">
                      {employee?.email ??
                        "Update profile, access, and security."}
                    </FormSheetDescription>
                  </div>
                </div>
                {/* mr-10 clears the sheet's close ✕ in the corner. */}
                <div className="mr-10 flex shrink-0 items-center">
                  <Select
                    value={status}
                    onValueChange={(v) =>
                      setStatus(v as "active" | "suspended")
                    }
                  >
                    <SelectTrigger
                      aria-label="Account status"
                      className="!h-9 !w-auto gap-2 !rounded-full !px-4 text-sm font-semibold focus:!ring-0 focus:!ring-offset-0 focus-visible:bg-foreground/5 focus-visible:outline-none"
                    >
                      {/* A div: the trigger line-clamps its direct spans. */}
                      <div className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className={cn(
                            "block h-2 w-2 shrink-0 rounded-full",
                            STATUS_DOT[status] ?? "bg-muted-foreground",
                          )}
                        />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={EDIT_EMPLOYEE_TABS}
              value={tab}
              onChange={setTab}
              label="Edit employee"
            />

            <FormSheetBody>
              {/* Every tab stays mounted, so values survive switching tabs;
                  hidden panels are display:none (DES 01-10-2026). */}
              <div {...formSheetTabPanel(tab, "profile")}>
                <FormSheetSection
                  hideHeading
                  title="Profile"
                  subtitle="Name and courtesy title for this team member."
                >
                  {/* Title, first and last name in one row, as Edit user. */}
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="Title">
                        <TitlePicker
                          value={title || null}
                          onChange={(label) => setTitle(label ?? "")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="First name">
                        <Input
                          capitalizeFirst
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          autoComplete="given-name"
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Last name">
                        <Input
                          capitalizeFirst
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          autoComplete="family-name"
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "access")}>
                {/* One section: role and the corporate card together (the
                    old Billing section), as Edit user's Access tab. */}
                <FormSheetSection
                  hideHeading
                  title="Access"
                  subtitle="Role controls permissions. The corporate card covers this employee’s billed sessions."
                >
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup
                        label="Role"
                        hint={canAssign ? "Company Admin can manage the team." : undefined}
                      >
                        <Select
                          value={role}
                          onValueChange={(v) => setRole(v as "user" | "admin")}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Company Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField span={2}>
                      {canAssign ? (
                        <FormFieldGroup
                          label="Corporate card"
                          hint="Sessions billed to the company will charge this card."
                        >
                          <Select
                            value={assignedCard}
                            onValueChange={setAssignedCard}
                          >
                            <SelectTrigger className="[&>span]:flex [&>span]:min-w-0 [&>span]:items-center [&>span]:overflow-hidden [&>span]:[display:flex] [&>span]:line-clamp-none">
                              <SelectValue>
                                {assignedCard === DEFAULT_CARD_VALUE ||
                                !selectedCard ? (
                                  <span className="inline-flex min-w-0 items-center gap-2">
                                    <CardBrandIcon brand={null} />
                                    <span className="truncate">
                                      Use company default
                                    </span>
                                  </span>
                                ) : (
                                  <span className="inline-flex min-w-0 items-center gap-2">
                                    <CardBrandIcon brand={selectedCard.brand} />
                                    <span className="truncate">
                                      •••• {selectedCard.last4} (
                                      {formatCardBrand(selectedCard.brand)})
                                      {selectedCard.isDefault
                                        ? ", default"
                                        : ""}
                                    </span>
                                  </span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={DEFAULT_CARD_VALUE}>
                                <span className="inline-flex items-center gap-2">
                                  <CardBrandIcon brand={null} />
                                  Use company default
                                </span>
                              </SelectItem>
                              {cards.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  <span className="inline-flex items-center gap-2">
                                    <CardBrandIcon brand={c.brand} />
                                    •••• {c.last4} ({formatCardBrand(c.brand)})
                                    {c.isDefault ? ", default" : ""}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormFieldGroup>
                      ) : (
                        <p className="py-2 text-sm text-muted-foreground">
                          Default company card applied automatically.
                        </p>
                      )}
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "security")}>
                <FormSheetSection
                  hideHeading
                  title="Security"
                  subtitle="Leave blank to keep the current password. Minimum 8 characters if set."
                >
                  <FormSheetFields>
                    <FormSheetField>
                      <FormFieldGroup label="New password (optional)">
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Leave blank to keep current"
                            autoComplete="new-password"
                            className="pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={saving}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving || !employee}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {saving ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </div>
      </FormSheetContent>
    </FormSheet>
  );
}

type EditEmployeeTab = "profile" | "access" | "security";

// Icons per tab, as Edit user (DES 01-10-2026: form standard).
const EDIT_EMPLOYEE_TABS: FormSheetTab<EditEmployeeTab>[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "access", label: "Access", icon: ShieldCheck },
  { id: "security", label: "Security", icon: KeyRound },
];

const STATUS_DOT: Record<string, string> = {
  active: "bg-success",
  suspended: "bg-warning",
};
